import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { printReceipt, downloadReceiptPDF, printOrderSummary, downloadOrderSummaryPDF } from "@/lib/receipt";
import { printReceiptRP820, connectRP820, isSerialSupported, getBaud, setBaud } from "@/lib/rp820";
import { ScanLine, Search, Trash2, Plus, Minus, X, CheckCircle2, Printer, Download, LogOut, ShieldOff, Settings, Upload, AlertTriangle, Camera } from "lucide-react";
import { CameraScanner } from "@/components/CameraScanner";
import { QuickAddModal } from "@/components/QuickAddModal";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

export default function POS() {
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [logging, setLogging] = useState(false);

  const refreshMe = useCallback(async () => {
    try { const r = await api.get("/auth/me"); setMe(r.data); }
    catch { setMe(null); }
    setChecking(false);
  }, []);
  useEffect(() => { refreshMe(); }, [refreshMe]);

  const doLogin = async (e) => {
    e.preventDefault();
    setLogging(true);
    try { await api.post("/auth/login", { email, password: pw }); await refreshMe(); toast.success("Signed in"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Login failed"); }
    setLogging(false);
  };
  const logout = async () => { try { await api.post("/auth/logout"); } catch (e) { console.error("Logout request failed", e); } setMe(null); };

  if (checking) return <div className="min-h-screen grid place-items-center text-slate-400">Loading Prepare Order…</div>;

  if (!me || !(me.permissions || []).includes("POS_SELL")) {
    return (
      <div className="min-h-screen grid place-items-center bg-brand-navy p-4">
        <div className="bg-white rounded-2xl p-8 w-full max-w-sm" data-testid="pos-login">
          <div className="text-brand-red font-black uppercase tracking-widest text-xs text-center">DealsWagon</div>
          <h1 className="font-heading text-2xl text-brand-navy text-center mt-1">Prepare Order Sign-in</h1>
          {me && !(me.permissions || []).includes("POS_SELL") ? (
            <div className="mt-4 text-center text-slate-600">
              <ShieldOff className="h-8 w-8 mx-auto text-brand-red" />
              <p className="mt-2 text-sm">Signed in as {me.email} — no Prepare Order access.</p>
              <button onClick={logout} className="mt-3 text-brand-navy font-bold underline">Switch account</button>
            </div>
          ) : (
            <form onSubmit={doLogin} className="mt-5 space-y-3">
              <input data-testid="pos-login-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-12 rounded-xl border border-slate-200 px-4" />
              <input data-testid="pos-login-password" type="password" required placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} className="w-full h-12 rounded-xl border border-slate-200 px-4" />
              <button data-testid="pos-login-submit" disabled={logging} className="w-full h-12 rounded-full bg-brand-navy text-white font-bold disabled:opacity-60">{logging ? "Signing in…" : "Sign in"}</button>
            </form>
          )}
        </div>
      </div>
    );
  }
  return <POSScreen me={me} onLogout={logout} />;
}

const POSScreen = ({ me, onLogout }) => {
  const [settings, setSettings] = useState({ hst_rate: 13, currency: "CAD" });
  const [categories, setCategories] = useState([]);
  const [cat, setCat] = useState("");
  const [results, setResults] = useState([]);
  const [cart, setCart] = useState([]);
  const [cartDiscount, setCartDiscount] = useState(0);
  const [cartDiscountPct, setCartDiscountPct] = useState(0);
  const [payOpen, setPayOpen] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [receiptCfg, setReceiptCfg] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [payLink, setPayLink] = useState(null);
  const [payEmail, setPayEmail] = useState("");
  const [pickList, setPickList] = useState(null);
  const [overrideItem, setOverrideItem] = useState(null);
  const [camOpen, setCamOpen] = useState(false);
  const [quickAdd, setQuickAdd] = useState(null);
  const canManage = (me?.permissions || []).includes("MANAGE_POS_SETTINGS");
  const scanRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    api.get("/pos/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/pos/search").then((r) => setResults(r.data.items)).catch(() => {});
    scanRef.current?.focus();
  }, []);

  const loadResults = useCallback((q, category) => {
    api.get("/pos/search", { params: { q: q || undefined, category_id: category || undefined } })
      .then((r) => setResults(r.data.items)).catch(() => {});
  }, []);

  const addToCart = useCallback((p, override = false) => {
    const oos = !p.inventory_id || p.available <= 0;
    if (oos && !override) { toast.error(`${p.product_name} is out of stock`); return; }
    if (!p.inventory_id) { toast.error(`${p.product_name} has no inventory record to sell`); return; }
    setCart((prev) => {
      const i = prev.findIndex((l) => l.product_id === p.product_id);
      if (i >= 0) {
        const line = prev[i];
        if (!line.override && line.quantity + 1 > p.available) { toast.error(`Only ${p.available} available`); return prev; }
        const copy = [...prev]; copy[i] = { ...line, quantity: line.quantity + 1 }; return copy;
      }
      return [...prev, { product_id: p.product_id, product_name: p.product_name, sku: p.sku, barcode: p.barcode,
        size: p.size, colour: p.colour, unit_price: p.price, quantity: 1, discount: 0, discPct: 0,
        available: override ? 999999 : p.available, override: !!override }];
    });
    toast.success(`Added ${p.product_name}${override ? " (oversell)" : ""}`);
  }, []);

  const handleCode = useCallback(async (q) => {
    q = (q || "").trim();
    if (!q) return;
    try {
      const r = await api.get("/pos/lookup", { params: { q } });
      const items = r.data.items || [];
      if (items.length === 1) addToCart(items[0]);
      else if (items.length > 1) setPickList(items);
      else setQuickAdd(q);
    } catch { toast.error("Product not found"); }
  }, [addToCart]);

  const scan = async (e) => {
    e.preventDefault();
    await handleCode(scanRef.current.value);
    scanRef.current.value = ""; scanRef.current.focus();
  };

  const setQty = (pid, qty) => setCart((prev) => prev.map((l) => {
    if (l.product_id !== pid) return l;
    const q = Math.max(1, Math.min(qty, l.available));
    if (qty > l.available) toast.error(`Only ${l.available} available`);
    const discount = l.discPct > 0 ? round2(l.unit_price * q * l.discPct / 100) : l.discount;
    return { ...l, quantity: q, discount };
  }));
  const setDisc = (pid, d) => setCart((prev) => prev.map((l) => l.product_id === pid ? { ...l, discount: Math.max(0, d), discPct: 0 } : l));
  const setDiscPct = (pid, pct) => setCart((prev) => prev.map((l) => {
    if (l.product_id !== pid) return l;
    const p = Math.max(0, Math.min(100, pct));
    return { ...l, discPct: p, discount: round2(l.unit_price * l.quantity * p / 100) };
  }));
  const remove = (pid) => setCart((prev) => prev.filter((l) => l.product_id !== pid));
  const clearCart = () => { setCart([]); setCartDiscount(0); setCartDiscountPct(0); };

  const addManualItem = (data) => {
    const finalCode = data.code && data.code.trim() ? `${data.code.trim()}M` : "M";
    setCart((prev) => [...prev, {
      product_id: `manual_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, manual: true,
      product_name: data.name, sku: finalCode, barcode: finalCode, size: "", colour: "",
      unit_price: data.price, compare_at_price: data.compareAt, quantity: data.quantity, discount: 0, discPct: 0, available: 9999,
    }]);
    toast.success(`Added ${data.name}`);
  };

  const createPayLink = async () => {
    if (!cart.length) return;
    try {
      const items = cart.map((l) => l.manual
        ? { manual: true, product_name: l.product_name, sku: l.sku, unit_price: l.unit_price, compare_at_price: l.compare_at_price, quantity: l.quantity, discount: l.discount }
        : { product_id: l.product_id, quantity: l.quantity, unit_price: l.unit_price, discount: l.discount, override: !!l.override });
      const r = await api.post("/pos/orders", { items, cart_discount: cartDiscount, customer_email: payEmail.trim() || undefined, origin_url: window.location.origin });
      setPayLink(r.data); clearCart(); setPayEmail("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create pay link"); }
  };

  const subtotal = cart.reduce((s, l) => s + Math.max(0, l.unit_price * l.quantity - l.discount), 0);
  useEffect(() => {
    if (cartDiscountPct > 0) setCartDiscount(round2(subtotal * cartDiscountPct / 100));
  }, [subtotal, cartDiscountPct]);
  const net = Math.max(0, subtotal - cartDiscount);
  const hst = +(net * (settings.hst_rate || 0) / 100).toFixed(2);
  const total = +(net + hst).toFixed(2);

  useEffect(() => {
    const h = (e) => {
      if (e.key === "F2") { e.preventDefault(); searchRef.current?.focus(); }
      else if (e.key === "F4") { e.preventDefault(); scanRef.current?.focus(); }
      else if (e.key === "F8") { e.preventDefault(); if (cart.length) setPayOpen(true); }
      else if (e.key === "Escape") { setPayOpen(false); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [cart.length]);

  const onComplete = (sale) => { setInvoice(sale); setPayOpen(false); clearCart(); loadResults("", cat); };

  return (
    <div className="h-screen overflow-hidden bg-slate-100 flex flex-col" data-testid="pos-screen">
      <header className="bg-brand-navy text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2"><ScanLine className="h-6 w-6 text-brand-yellow" /><span className="font-heading text-lg">DealsWagon Prepare Order</span></div>
        <div className="flex items-center gap-4 text-sm">
          <span className="hidden sm:inline opacity-80">{me.name || me.email} · {me.role}</span>
          {canManage && <button onClick={() => setReceiptCfg(true)} data-testid="pos-receipt-settings-btn" className="inline-flex items-center gap-1 opacity-80 hover:opacity-100"><Settings className="h-4 w-4" /> Order Summary</button>}
          {canManage && <a href="/scan-photos" data-testid="pos-scan-photos-link" className="inline-flex items-center gap-1 opacity-80 hover:opacity-100"><Camera className="h-4 w-4" /> Photos</a>}
          <a href="/admin" className="opacity-80 hover:opacity-100 hidden sm:inline">Admin</a>
          <button onClick={onLogout} data-testid="pos-logout" className="inline-flex items-center gap-1 hover:text-brand-yellow"><LogOut className="h-4 w-4" /> Exit</button>
        </div>
      </header>

      <div className="flex-1 grid lg:grid-cols-[1.3fr_1fr] gap-3 p-3 overflow-hidden">
        {/* LEFT: scan / search / results */}
        <div className="flex flex-col gap-3 min-h-0">
          <form onSubmit={scan} className="bg-white rounded-xl p-3 flex gap-2 items-center shadow-sm">
            <ScanLine className="h-6 w-6 text-brand-green shrink-0" />
            <input ref={scanRef} data-testid="pos-scan-input" placeholder="Scan barcode (F4) then Enter…" className="flex-1 h-12 text-lg px-3 rounded-lg border border-slate-200 focus:border-brand-green outline-none" autoComplete="off" />
            <button type="button" onClick={() => setCamOpen(true)} data-testid="pos-camera-scan-btn" className="h-12 px-3 rounded-lg border border-brand-navy text-brand-navy font-bold inline-flex items-center gap-1" title="Scan with phone camera"><Camera className="h-5 w-5" /><span className="hidden sm:inline">Camera</span></button>
            <button className="h-12 px-5 rounded-lg bg-brand-green text-white font-bold">Add</button>
          </form>
          <div className="bg-white rounded-xl p-3 flex gap-2 items-center shadow-sm">
            <Search className="h-5 w-5 text-slate-400 shrink-0" />
            <input ref={searchRef} data-testid="pos-search-input" placeholder="Search products (F2)…" onChange={(e) => loadResults(e.target.value, cat)} className="flex-1 h-11 px-3 rounded-lg border border-slate-200 outline-none" />
            <select data-testid="pos-category-filter" value={cat} onChange={(e) => { setCat(e.target.value); loadResults(searchRef.current?.value, e.target.value); }} className="h-11 px-2 rounded-lg border border-slate-200 text-sm">
              <option value="">All categories</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="bg-white rounded-xl p-3 shadow-sm flex-1 overflow-y-auto min-h-0" data-testid="pos-results">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {results.map((p) => {
                const oos = !p.inventory_id || p.available <= 0;
                return (
                  <button key={p.product_id} onClick={() => { if (!p.inventory_id) { toast.error("No inventory record"); return; } oos ? setOverrideItem(p) : addToCart(p); }} data-testid={`pos-result-${p.product_id}`}
                    className={`text-left border rounded-lg p-2 transition ${oos ? "border-amber-300 bg-amber-50/50 hover:border-brand-red" : "border-slate-200 hover:border-brand-navy hover:shadow"}`}>
                    <div className="font-bold text-sm text-brand-navy line-clamp-2">{p.product_name}</div>
                    <div className="text-[11px] text-slate-500">{[p.brand, p.size, p.colour].filter(Boolean).join(" · ")}</div>
                    <div className="text-[11px] text-slate-400">{p.sku}</div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="flex items-baseline gap-1">
                        <span className="font-black text-brand-navy">{money(p.price)}</span>
                        {p.compare_at_price && p.compare_at_price > p.price && <span className="text-[10px] text-slate-400 line-through">{money(p.compare_at_price)}</span>}
                      </span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${oos ? "bg-red-100 text-red-700" : p.available <= (settings.low_stock_threshold || 3) ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
                        {oos ? "OUT · sell anyway" : `${p.available} left`}
                      </span>
                    </div>
                  </button>
                );
              })}
              {!results.length && <div className="col-span-full text-center text-slate-400 py-8">No products.</div>}
            </div>
          </div>
        </div>

        {/* RIGHT: cart */}
        <div className="bg-white rounded-xl shadow-sm flex flex-col min-h-0" data-testid="pos-cart">
          <div className="p-3 border-b flex items-center justify-between">
            <span className="font-heading text-lg text-brand-navy">Current Order</span>
            <div className="flex items-center gap-3">
              <button onClick={() => setShowManual(true)} data-testid="pos-manual-item-btn" className="text-sm text-brand-navy font-bold inline-flex items-center gap-1"><Plus className="h-4 w-4" /> Manual item</button>
              {cart.length > 0 && <button onClick={clearCart} data-testid="pos-clear-cart" className="text-sm text-brand-red font-bold inline-flex items-center gap-1"><Trash2 className="h-4 w-4" /> Clear</button>}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto min-h-0 divide-y">
            {cart.length === 0 && <div className="text-center text-slate-400 py-12">Scan or tap a product to start.</div>}
            {cart.map((l) => (
              <div key={l.product_id} className="p-3" data-testid={`pos-cart-line-${l.product_id}`}>
                <div className="flex justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-bold text-sm text-brand-navy truncate">{l.product_name}{l.override && <span className="ml-1 text-[10px] font-black text-brand-red align-middle">OVERSELL</span>}</div>
                    <div className="text-[11px] text-slate-500">{[l.sku, l.size && `Sz ${l.size}`, l.colour].filter(Boolean).join(" · ")}</div>
                  </div>
                  <button onClick={() => remove(l.product_id)} data-testid={`pos-remove-${l.product_id}`} className="text-slate-400 hover:text-brand-red shrink-0"><Trash2 className="h-4 w-4" /></button>
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <div className="flex items-center border rounded-lg">
                    <button onClick={() => setQty(l.product_id, l.quantity - 1)} className="px-2 py-1 text-slate-600"><Minus className="h-4 w-4" /></button>
                    <input type="number" value={l.quantity} onChange={(e) => setQty(l.product_id, parseInt(e.target.value) || 1)} data-testid={`pos-qty-${l.product_id}`} className="w-12 text-center outline-none" />
                    <button onClick={() => setQty(l.product_id, l.quantity + 1)} className="px-2 py-1 text-slate-600"><Plus className="h-4 w-4" /></button>
                  </div>
                  <span className="text-sm text-slate-500">× {money(l.unit_price)}</span>
                  <div className="ml-auto flex items-center gap-2">
                    <label className="text-xs text-slate-500 flex items-center gap-1">Disc
                      <input type="number" value={l.discPct || 0} onChange={(e) => setDiscPct(l.product_id, parseFloat(e.target.value) || 0)} data-testid={`pos-disc-pct-${l.product_id}`} className="w-12 h-7 border rounded px-1 text-right" />%
                    </label>
                    <label className="text-xs text-slate-500 flex items-center gap-1">$
                      <input type="number" value={l.discount} onChange={(e) => setDisc(l.product_id, parseFloat(e.target.value) || 0)} data-testid={`pos-disc-${l.product_id}`} className="w-16 h-7 border rounded px-1 text-right" />
                    </label>
                  </div>
                  <span className="font-bold text-brand-navy w-20 text-right">{money(Math.max(0, l.unit_price * l.quantity - l.discount))}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="p-3 border-t space-y-1 bg-slate-50 rounded-b-xl shrink-0">
            <Row label="Subtotal" value={money(subtotal)} />
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Cart discount</span>
              <span className="flex items-center gap-1">
                <input type="number" value={cartDiscountPct} onChange={(e) => setCartDiscountPct(Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))} data-testid="pos-cart-discount-pct" className="w-12 h-7 border rounded px-1 text-right" />%
                <span className="ml-1">$</span>
                <input type="number" value={cartDiscount} onChange={(e) => { setCartDiscount(Math.max(0, parseFloat(e.target.value) || 0)); setCartDiscountPct(0); }} data-testid="pos-cart-discount" className="w-20 h-7 border rounded px-1 text-right" />
              </span>
            </div>
            <Row label={`HST (${settings.hst_rate}%)`} value={money(hst)} />
            <div className="flex items-center justify-between pt-1 border-t mt-1">
              <span className="font-heading text-xl text-brand-navy">TOTAL</span>
              <span className="font-heading text-2xl text-brand-navy" data-testid="pos-total">{money(total)}</span>
            </div>
            <input type="email" value={payEmail} onChange={(e) => setPayEmail(e.target.value)} placeholder="Customer email (optional — emails the pay link + invoice)" data-testid="pos-customer-email" className="w-full h-10 mt-2 border border-slate-200 rounded-lg px-3 text-sm" />
            <button onClick={createPayLink} disabled={!cart.length} data-testid="pos-complete-btn"
              className="w-full h-14 mt-2 rounded-xl bg-brand-red text-white font-black text-lg disabled:opacity-40 hover:bg-brand-redHover">
              CREATE PAY LINK (F8)
            </button>
          </div>
        </div>
      </div>

      {payOpen && <PaymentModal total={total} cart={cart} cartDiscount={cartDiscount} settings={settings} onClose={() => setPayOpen(false)} onComplete={onComplete} />}
      {invoice && <InvoiceModal sale={invoice} settings={settings} onClose={() => setInvoice(null)} />}
      {receiptCfg && <ReceiptSettingsModal settings={settings} onClose={() => setReceiptCfg(false)} onSaved={(ns) => setSettings(ns)} />}
      {showManual && <ManualItemModal onAdd={addManualItem} onClose={() => setShowManual(false)} />}
      {payLink && <PayLinkModal order={payLink} settings={settings} onClose={() => setPayLink(null)} />}
      {pickList && <ItemPickerModal items={pickList} lowThreshold={settings.low_stock_threshold || 3}
        onPick={(p) => { addToCart(p); setPickList(null); }}
        onOverride={(p) => { setPickList(null); setOverrideItem(p); }} onClose={() => setPickList(null)} />}
      {overrideItem && <OverrideModal item={overrideItem}
        onConfirm={() => { addToCart(overrideItem, true); setOverrideItem(null); }} onClose={() => setOverrideItem(null)} />}
      {camOpen && <CameraScanner title="Scan to ring up" onDetect={handleCode} onClose={() => { setCamOpen(false); scanRef.current?.focus(); }} />}
      {quickAdd !== null && <QuickAddModal code={quickAdd} onClose={() => setQuickAdd(null)}
        onCreated={(item) => { addToCart(item, (item.available || 0) <= 0); setQuickAdd(null); }} />}
    </div>
  );
};

// OOS override: confirm selling an item that shows no available stock (oversell).
const OverrideModal = ({ item, onConfirm, onClose }) => (
  <div className="fixed inset-0 z-[100] bg-black/60 grid place-items-center p-4" onClick={onClose}>
    <div className="relative bg-white rounded-2xl p-6 w-full max-w-sm text-center" onClick={(e) => e.stopPropagation()} data-testid="override-modal">
      <button onClick={onClose} data-testid="override-close-x" className="absolute top-3 right-3 text-slate-400 hover:text-brand-red"><X className="h-5 w-5" /></button>
      <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto" />
      <h2 className="font-heading text-2xl text-brand-navy mt-2">Out of stock</h2>
      <p className="text-sm text-slate-600 mt-2"><b>{item.product_name}</b> shows <b>{item.available}</b> in stock. Selling it will take inventory to <b>{item.available - 1}</b>. The order is still recorded, and a return will add the unit back.</p>
      <div className="grid grid-cols-2 gap-2 mt-5">
        <button onClick={onClose} data-testid="override-cancel" className="h-12 rounded-lg border border-slate-200 text-slate-600 font-bold">Cancel</button>
        <button onClick={onConfirm} data-testid="override-confirm" className="h-12 rounded-lg bg-brand-red text-white font-bold">Sell anyway</button>
      </div>
    </div>
  </div>
);

// Shown when a scanned/typed code matches 2+ items (duplicate SKUs). The cashier taps the one to ring.
const ItemPickerModal = ({ items, lowThreshold, onPick, onOverride, onClose }) => {
  const locLine = (p) => {
    const sl = p.storage_location || {};
    return [p.location_name, sl.aisle && `Aisle ${sl.aisle}`, sl.rack && `Rack ${sl.rack}`, sl.shelf && `Shelf ${sl.shelf}`, sl.bin && `Bin ${sl.bin}`].filter(Boolean).join(" · ");
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="pos-item-picker">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="font-heading text-xl text-brand-navy">Choose item</h2>
            <p className="text-xs text-slate-500 mt-0.5">{items.length} items share this code — tap the one you're selling</p>
          </div>
          <button onClick={onClose} data-testid="pos-item-picker-close"><X className="h-5 w-5 text-slate-500" /></button>
        </div>
        <div className="p-3 overflow-y-auto divide-y">
          {items.map((p) => {
            const oos = !p.inventory_id || p.available <= 0;
            return (
              <button key={p.product_id} onClick={() => { if (!p.inventory_id) return; oos ? onOverride(p) : onPick(p); }} disabled={!p.inventory_id} data-testid={`pos-pick-${p.product_id}`}
                className={`w-full text-left p-3 flex items-start justify-between gap-3 transition ${!p.inventory_id ? "opacity-60 cursor-not-allowed" : "hover:bg-slate-50"}`}>
                <div className="min-w-0">
                  <div className="font-bold text-brand-navy">{p.product_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{[p.sku, p.purchase_receipt && `Receipt ${p.purchase_receipt}`].filter(Boolean).join(" · ")}</div>
                  {locLine(p) && <div className="text-xs text-slate-400 mt-0.5">{locLine(p)}</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className="flex items-baseline gap-1 justify-end">
                    <span className="font-black text-brand-navy text-lg">{money(p.price)}</span>
                    {p.compare_at_price && p.compare_at_price > p.price && <span className="text-[11px] text-slate-400 line-through">{money(p.compare_at_price)}</span>}
                  </div>
                  <span className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${oos ? "bg-red-100 text-red-700" : p.available <= lowThreshold ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
                    {oos ? "OUT · sell anyway" : `${p.available} available`}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm"><span className="text-slate-500">{label}</span><span className="font-medium">{value}</span></div>
);

const PAY = ["Cash", "Debit", "Credit Card", "E-Transfer", "Other"];

const PaymentModal = ({ total, cart, cartDiscount, settings, onClose, onComplete }) => {
  const [method, setMethod] = useState("Cash");
  const [cash, setCash] = useState("");
  const [busy, setBusy] = useState(false);
  const change = method === "Cash" && cash !== "" ? +(parseFloat(cash) - total).toFixed(2) : null;

  const submit = async () => {
    if (method === "Cash" && (cash === "" || parseFloat(cash) < total)) { toast.error("Enter cash received (≥ total)"); return; }
    setBusy(true);
    try {
      const items = cart.map((l) => l.manual
        ? { manual: true, product_name: l.product_name, sku: l.sku, unit_price: l.unit_price, compare_at_price: l.compare_at_price, quantity: l.quantity, discount: l.discount }
        : { product_id: l.product_id, quantity: l.quantity, unit_price: l.unit_price, discount: l.discount, override: !!l.override });
      const r = await api.post("/pos/sales", { items, payment_method: method, cart_discount: cartDiscount, cash_received: method === "Cash" ? parseFloat(cash) : null });
      toast.success(`Order ${r.data.invoice_number} complete`);
      onComplete(r.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Order failed"); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="pos-payment-modal">
        <div className="flex justify-between items-center"><h2 className="font-heading text-xl text-brand-navy">Payment</h2><button onClick={onClose}><X className="h-5 w-5" /></button></div>
        <div className="text-center my-4"><div className="text-slate-500 text-sm">Amount due</div><div className="font-heading text-4xl text-brand-navy">{money(total)}</div></div>
        <div className="grid grid-cols-3 gap-2">
          {PAY.map((m) => (
            <button key={m} onClick={() => setMethod(m)} data-testid={`pos-pay-${m.replace(/\s/g, "-").toLowerCase()}`}
              className={`h-12 rounded-lg text-sm font-bold border ${method === m ? "bg-brand-navy text-white border-brand-navy" : "border-slate-200 text-slate-600"}`}>{m}</button>
          ))}
        </div>
        {method === "Cash" && (
          <div className="mt-4">
            <label className="text-sm text-slate-500">Cash received</label>
            <input type="number" autoFocus value={cash} onChange={(e) => setCash(e.target.value)} data-testid="pos-cash-received" className="w-full h-12 border rounded-lg px-3 text-lg mt-1" placeholder="0.00" />
            {change !== null && <div className={`mt-2 text-center font-bold text-lg ${change < 0 ? "text-brand-red" : "text-brand-green"}`} data-testid="pos-change">Change: {money(Math.max(0, change))}</div>}
          </div>
        )}
        <button onClick={submit} disabled={busy} data-testid="pos-confirm-sale" className="w-full h-14 mt-5 rounded-xl bg-brand-green text-white font-black text-lg disabled:opacity-50">
          {busy ? "Processing…" : "CONFIRM & PRINT"}
        </button>
      </div>
    </div>
  );
};

export const InvoiceModal = ({ sale, settings, onClose, showClose = true }) => {
  const [printing, setPrinting] = useState(false);
  const [setup, setSetup] = useState(false);
  const [baud, setBaudState] = useState(getBaud());
  const serialOk = isSerialSupported();

  const thermalPrint = async () => {
    if (!serialOk) { toast.error("Thermal printing needs Chrome or Edge — using browser print."); printReceipt(sale, settings); return; }
    setPrinting(true);
    try { await printReceiptRP820(sale, settings); toast.success("Sent to RP820 printer"); }
    catch (e) { console.error("RP820 print failed", e); toast.error(e?.message || "Could not reach the RP820 printer"); }
    setPrinting(false);
  };
  const connect = async () => {
    try { setBaud(baud); await connectRP820(); toast.success("RP820 connected"); }
    catch (e) { console.error("RP820 connect failed", e); toast.error(e?.message || "Printer selection cancelled"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <div className="relative bg-white rounded-2xl p-6 w-full max-w-md text-center" onClick={(e) => e.stopPropagation()} data-testid="pos-invoice-modal">
        <button onClick={onClose} data-testid="pos-invoice-close-x" className="absolute top-3 right-3 text-slate-400 hover:text-brand-red"><X className="h-5 w-5" /></button>
        <CheckCircle2 className="h-12 w-12 text-brand-green mx-auto" />
        <h2 className="font-heading text-2xl text-brand-navy mt-2">Order Complete</h2>
        <div className="text-slate-500">Invoice <b className="text-brand-navy" data-testid="pos-invoice-number">{sale.invoice_number}</b></div>
        <div className="text-xs text-slate-400 mt-0.5" data-testid="pos-invoice-datetime">{new Date(sale.created_at || Date.now()).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" })}</div>
        <div className="font-heading text-3xl text-brand-navy mt-2">{money(sale.total)}</div>
        {sale.payment_method === "Cash" && <div className="text-slate-600 mt-1">Change: <b>{money(sale.change_due)}</b></div>}
        <div className="grid grid-cols-2 gap-2 mt-5">
          <button onClick={() => printReceipt(sale, settings)} data-testid="pos-print-invoice" className="h-12 rounded-lg bg-brand-navy text-white font-bold inline-flex items-center justify-center gap-2"><Printer className="h-4 w-4" /> Print order summary</button>
          <button onClick={() => downloadReceiptPDF(sale, settings)} data-testid="pos-download-invoice" className="h-12 rounded-lg border border-brand-navy text-brand-navy font-bold inline-flex items-center justify-center gap-2"><Download className="h-4 w-4" /> PDF</button>
        </div>
        <div className="flex items-center justify-end mt-2 text-xs text-slate-500">
          <button onClick={() => setSetup((v) => !v)} data-testid="pos-printer-setup-toggle" className="inline-flex items-center gap-1 hover:text-brand-navy"><Settings className="h-3.5 w-3.5" /> Printer options</button>
        </div>
        {setup && (
          <div className="mt-2 bg-slate-50 rounded-lg p-3 text-left" data-testid="pos-printer-setup">
            <div className="text-xs font-black uppercase tracking-widest text-brand-navy">Printer options</div>
            <p className="text-[11px] text-slate-500 mt-1"><b>Print order summary</b> uses your installed printer driver — the printer you see in the Ctrl+P list. This is the recommended way for a USB-connected RP820. In the print dialog pick the RP820 and set paper to the 80mm roll.</p>
            <div className="mt-2 border-t pt-2">
              <div className="text-xs font-bold text-brand-navy">Advanced — direct serial (ESC/POS)</div>
              <p className="text-[11px] text-slate-500 mt-0.5">Use this only if the RP820 is connected as a <b>COM / serial port</b> (not via the USB printer driver). Most USB setups should use <b>Print order summary</b> above.</p>
              {!serialOk && <div className="text-xs text-brand-red mt-1">This browser can't use serial printing — use Chrome or Edge.</div>}
              <label className="text-xs text-slate-600 flex items-center gap-2 mt-2">Baud rate
                <select value={baud} onChange={(e) => setBaudState(parseInt(e.target.value, 10))} data-testid="pos-printer-baud" className="h-8 border rounded px-2">
                  {[9600, 19200, 38400, 115200].map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </label>
              <div className="flex gap-2 mt-2">
                <button onClick={connect} disabled={!serialOk} data-testid="pos-printer-connect" className="h-9 px-3 rounded-lg border border-brand-navy text-brand-navy text-sm font-bold disabled:opacity-50">Choose COM port</button>
                <button onClick={thermalPrint} disabled={!serialOk || printing} data-testid="pos-serial-print" className="h-9 px-3 rounded-lg bg-brand-green text-white text-sm font-bold disabled:opacity-50">{printing ? "Printing…" : "Serial print"}</button>
              </div>
            </div>
          </div>
        )}
        {showClose && <button onClick={onClose} data-testid="pos-new-sale" className="w-full h-12 mt-3 rounded-lg bg-brand-green text-white font-bold">New Order</button>}
      </div>
    </div>
  );
};

// Downscale an uploaded logo onto a white background and return a PNG data URL (keeps receipts crisp + payload small).
const PayLinkModal = ({ order, settings = {}, onClose }) => {
  const [email, setEmail] = useState(order.customer_email || "");
  const [sending, setSending] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(order.pay_url); toast.success("Link copied"); };
  const sendEmail = async () => {
    if (!email.trim()) { toast.error("Enter a customer email"); return; }
    setSending(true);
    try { await api.post(`/pos/orders/${order.order_id}/send-link`, { email: email.trim() }); toast.success(`Pay link emailed to ${email.trim()}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send email"); }
    setSending(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <div className="relative bg-white rounded-2xl p-6 w-full max-w-sm text-center" onClick={(e) => e.stopPropagation()} data-testid="pos-paylink-modal">
        <button onClick={onClose} data-testid="pos-paylink-close-x" className="absolute top-3 right-3 text-slate-400 hover:text-brand-red"><X className="h-5 w-5" /></button>
        <CheckCircle2 className="h-10 w-10 text-brand-green mx-auto" />
        <h2 className="font-heading text-xl text-brand-navy mt-1">Pay link ready</h2>
        <div className="font-heading text-2xl text-brand-navy mt-1">{money(order.total)}</div>
        <p className="text-xs text-slate-500">Stock held for 24h. Show this QR for the customer to scan.</p>
        <img src={order.qr_data_url} alt="QR code" data-testid="pos-paylink-qr" className="h-52 w-52 mx-auto my-3" />
        <div className="flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2">
          <input readOnly value={order.pay_url} data-testid="pos-paylink-url" className="flex-1 bg-transparent text-xs text-slate-600 outline-none" />
          <button onClick={copy} data-testid="pos-paylink-copy" className="text-brand-navy font-bold text-sm">Copy</button>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Customer email" data-testid="pos-paylink-email" className="flex-1 h-10 border border-slate-200 rounded-lg px-3 text-sm" />
          <button onClick={sendEmail} disabled={sending} data-testid="pos-paylink-send" className="h-10 px-4 rounded-lg bg-brand-navy text-white font-bold text-sm disabled:opacity-60">{sending ? "…" : "Email"}</button>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <button onClick={() => printOrderSummary(order, settings)} data-testid="pos-paylink-print" className="h-11 rounded-lg bg-brand-navy text-white font-bold inline-flex items-center justify-center gap-2"><Printer className="h-4 w-4" /> Print summary</button>
          <button onClick={() => downloadOrderSummaryPDF(order, settings)} data-testid="pos-paylink-pdf" className="h-11 rounded-lg border border-brand-navy text-brand-navy font-bold inline-flex items-center justify-center gap-2"><Download className="h-4 w-4" /> PDF</button>
        </div>
        <a href={order.pay_url} target="_blank" rel="noreferrer" data-testid="pos-paylink-open" className="block mt-2 text-sm text-brand-navy underline">Open pay page</a>
        <button onClick={onClose} data-testid="pos-paylink-new" className="w-full h-12 mt-4 rounded-lg bg-brand-green text-white font-bold">New Order</button>
      </div>
    </div>
  );
};

// Downscale an uploaded logo onto a white background and return a PNG data URL (keeps receipts crisp + payload small).
const resizeImage = (file, maxW, maxH) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new window.Image();
    img.onload = () => {
      const ratio = Math.min(maxW / img.width, maxH / img.height, 1);
      const width = Math.round(img.width * ratio), height = Math.round(img.height * ratio);
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = reader.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const RcField = ({ label, k, value, onChange, placeholder }) => (
  <label className="block text-left">
    <span className="text-xs font-bold text-slate-600">{label}</span>
    <input value={value} onChange={onChange} placeholder={placeholder} data-testid={`pos-receipt-${k.replace(/_/g, "-")}`}
      className="w-full h-10 mt-1 border border-slate-200 rounded-lg px-3 text-sm outline-none focus:border-brand-navy" />
  </label>
);

const ManualItemModal = ({ onAdd, onClose }) => {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [price, setPrice] = useState("");
  const [compareAt, setCompareAt] = useState("");
  const [qty, setQty] = useState(1);

  const submit = () => {
    if (!name.trim()) { toast.error("Enter an item name"); return; }
    const p = parseFloat(price);
    if (isNaN(p) || p < 0) { toast.error("Enter a valid price"); return; }
    const c = compareAt === "" ? null : parseFloat(compareAt);
    onAdd({
      name: name.trim(), code: code.trim(), price: +p.toFixed(2),
      compareAt: c != null && !isNaN(c) && c > 0 ? +c.toFixed(2) : null,
      quantity: Math.max(1, parseInt(qty) || 1),
    });
    onClose();
  };

  const fld = "w-full h-11 border border-slate-200 rounded-lg px-3 text-sm outline-none focus:border-brand-navy";
  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="pos-manual-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-heading text-xl text-brand-navy">Manual item</h2>
          <button onClick={onClose} data-testid="pos-manual-close"><X className="h-5 w-5 text-slate-500" /></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="text-xs font-bold text-slate-600">Item name *</span>
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} data-testid="pos-manual-name" className={`${fld} mt-1`} placeholder="e.g. Assorted clearance item" />
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-600">Item code (optional)</span>
            <input value={code} onChange={(e) => setCode(e.target.value)} data-testid="pos-manual-code" className={`${fld} mt-1`} placeholder="Prints on the receipt · defaults to MANUAL" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-bold text-slate-600">Our price *</span>
              <input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="pos-manual-price" className={`${fld} mt-1 text-right`} placeholder="0.00" />
            </label>
            <label className="block">
              <span className="text-xs font-bold text-slate-600">Compare-at price</span>
              <input type="number" min="0" step="0.01" value={compareAt} onChange={(e) => setCompareAt(e.target.value)} data-testid="pos-manual-compare" className={`${fld} mt-1 text-right`} placeholder="optional" />
            </label>
          </div>
          <label className="block w-32">
            <span className="text-xs font-bold text-slate-600">Quantity</span>
            <input type="number" min="1" step="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="pos-manual-qty" className={`${fld} mt-1 text-right`} />
          </label>
        </div>
        <div className="px-5 py-4 border-t flex justify-end gap-2">
          <button onClick={onClose} className="h-11 px-4 rounded-lg border border-slate-200 text-slate-600 font-bold">Cancel</button>
          <button onClick={submit} data-testid="pos-manual-add" className="h-11 px-5 rounded-lg bg-brand-green text-white font-bold inline-flex items-center gap-2"><Plus className="h-4 w-4" /> Add to bill</button>
        </div>
      </div>
    </div>
  );
};

const ReceiptSettingsModal = ({ settings, onClose, onSaved }) => {
  const [form, setForm] = useState({
    store_name: settings.store_name || "", store_address_block: settings.store_address_block || "",
    store_phone: settings.store_phone || "",
    store_website: settings.store_website || "",
    store_tax_number: settings.store_tax_number || "",
    invoice_footer: settings.invoice_footer || "", return_policy: settings.return_policy || "",
    store_etransfer: settings.store_etransfer || "", paylink_message: settings.paylink_message || "",
    receipt_logo: settings.receipt_logo || "",
  });
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    try { const dataUrl = await resizeImage(f, 480, 240); setForm((p) => ({ ...p, receipt_logo: dataUrl })); }
    catch { toast.error("Could not read that image"); }
  };

  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/pos/settings", form); onSaved(r.data); toast.success("Receipt settings saved"); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save settings"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="pos-receipt-settings-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white">
          <h2 className="font-heading text-xl text-brand-navy">Order summary branding</h2>
          <button onClick={onClose} data-testid="pos-receipt-close"><X className="h-5 w-5 text-slate-500" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <span className="text-xs font-bold text-slate-600">Logo (top of receipt)</span>
            <div className="mt-1 flex items-center gap-4">
              <div className="h-20 w-40 border border-dashed border-slate-300 rounded-lg grid place-items-center bg-slate-50 overflow-hidden">
                {form.receipt_logo
                  ? <img src={form.receipt_logo} alt="logo preview" data-testid="pos-receipt-logo-preview" className="max-h-full max-w-full object-contain" />
                  : <span className="text-xs text-slate-400">No logo</span>}
              </div>
              <div className="flex flex-col gap-2">
                <input ref={fileRef} type="file" accept="image/*" onChange={onFile} data-testid="pos-receipt-logo-input" className="hidden" />
                <button onClick={() => fileRef.current?.click()} data-testid="pos-receipt-logo-upload" className="h-9 px-3 rounded-lg bg-brand-navy text-white text-sm font-bold inline-flex items-center gap-2"><Upload className="h-4 w-4" /> Upload logo</button>
                {form.receipt_logo && <button onClick={() => setForm((p) => ({ ...p, receipt_logo: "" }))} data-testid="pos-receipt-logo-remove" className="h-9 px-3 rounded-lg border border-brand-red text-brand-red text-sm font-bold inline-flex items-center gap-2"><Trash2 className="h-4 w-4" /> Remove</button>}
              </div>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">PNG or JPG, centered at the top. Wide/landscape logos print best.</p>
          </div>
          <RcField label="Store name" k="store_name" value={form.store_name} onChange={set("store_name")} placeholder="DealsWagon Outlet" />
          <RcField label="Website" k="store_website" value={form.store_website} onChange={set("store_website")} placeholder="www.dealswagon.ca" />
          <label className="block">
            <span className="text-xs font-bold text-slate-600">Address / location (one line per receipt line)</span>
            <textarea value={form.store_address_block} onChange={set("store_address_block")} rows={4} data-testid="pos-setting-store_address_block"
              placeholder={"Barrhaven, Ottawa, Ontario\nDymon Storage – Barrhaven Drive-Through · Store Units 4 & 5"}
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            <p className="text-[11px] text-slate-400 mt-1">Each new line prints as its own line, centered and word-wrapped on the receipt.</p>
          </label>
          <RcField label="Phone" k="store_phone" value={form.store_phone} onChange={set("store_phone")} placeholder="(613) 000-0000" />
          <RcField label="HST/GST number" k="store_tax_number" value={form.store_tax_number} onChange={set("store_tax_number")} placeholder="123456789 RT0001" />
          <RcField label="Footer message" k="invoice_footer" value={form.invoice_footer} onChange={set("invoice_footer")} placeholder="Thank you for shopping with us!" />
          <label className="block text-left">
            <span className="text-xs font-bold text-slate-600">Interac e-Transfer details (shown to customers on the pay page & order summary)</span>
            <textarea value={form.store_etransfer} onChange={set("store_etransfer")} rows={3} data-testid="pos-setting-store_etransfer"
              placeholder={"Send Interac e-Transfer to pay@dealswagon.ca (auto-deposit).\nUse your order number as the message — no security question needed."}
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-brand-navy" />
          </label>
          <label className="block text-left">
            <span className="text-xs font-bold text-slate-600">Pay-link message (email + pay page). Leave blank to use the default wording.</span>
            <textarea value={form.paylink_message} onChange={set("paylink_message")} rows={2} data-testid="pos-setting-paylink_message"
              placeholder={"Thanks for shopping with DealsWagon! Tap below to review your items and pay securely."}
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-brand-navy" />
          </label>
          <label className="block text-left">
            <span className="text-xs font-bold text-slate-600">Terms &amp; Conditions (prints on receipts & order summary)</span>
            <textarea value={form.return_policy} onChange={set("return_policy")} rows={3} data-testid="pos-receipt-return-policy"
              placeholder={"All sales are final.\nReturns accepted within 14 days with receipt."}
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-brand-navy" />
            <p className="text-[11px] text-slate-400 mt-1">Each new line prints as its own line, word-wrapped and centered.</p>
          </label>
        </div>
        <div className="px-5 py-4 border-t sticky bottom-0 bg-white flex justify-end gap-2">
          <button onClick={onClose} className="h-11 px-4 rounded-lg border border-slate-200 text-slate-600 font-bold">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="pos-receipt-save" className="h-11 px-5 rounded-lg bg-brand-green text-white font-bold disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
};
